const COLUMN_LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

export const getColumnLetter = (index: number) => {
	if (index < 26) return COLUMN_LETTERS[index];
	return `${COLUMN_LETTERS[Math.floor(index / 26) - 1]}${COLUMN_LETTERS[index % 26]}`;
};

// Domyślna nazwa kolumny, gdy plik CSV nie ma nagłówków (np. "Kolumna A").
export const generateColumnName = (index: number) =>
	`Kolumna ${getColumnLetter(index)}`;

export const SPREADSHEET_ACCEPT =
	// Android wyszarza CSV przy samym ".csv" — dorzucamy warianty MIME
	".csv,.xlsx,text/csv,text/comma-separated-values,application/csv,application/vnd.ms-excel,text/plain,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

// Linie komórki zaczynające się od "> " (jak w Markdown) → <blockquote>; sąsiednie linie cytatu to jeden blok.
// SheetJS oddaje "\n" jako <br/>, a ">" jako &gt; (zwykła komórka) lub surowe ">" (komórka z formatowaniem);
// linia może zaczynać się od tagów formatowania (<span><i>).
// ponytail: tag (<b>/<i>) rozciągnięty na kilka linii zostaje niedomknięty w bloku — parser HTML sam go odtwarza
const quoteLinesToHtml = (html: string) => {
	if (!/(^|<br\/>)(<[^>]+>)*\s*(&gt;|>)/.test(html)) return html;
	const blocks: string[] = [];
	let text: string[] = [];
	let quote: string[] = [];
	const flush = () => {
		if (text.length) blocks.push(`<p>${text.join("<br/>")}</p>`);
		if (quote.length)
			blocks.push(
				`<blockquote>${quote.map((l) => `<p>${l}</p>`).join("")}</blockquote>`,
			);
		text = [];
		quote = [];
	};
	for (const line of html.split("<br/>")) {
		const m = line.match(/^((?:<[^>]+>)*)\s*(?:&gt;|>)\s?(.*)$/);
		if (m) {
			if (text.length) flush();
			quote.push(m[1] + m[2]);
		} else {
			if (quote.length) flush();
			text.push(line);
		}
	}
	flush();
	return blocks.join("");
};

const readXlsxRows = async (file: Blob): Promise<string[][]> => {
	const XLSX = await import("xlsx");
	const wb = XLSX.read(await file.arrayBuffer(), { type: "array" });
	const ws = wb.Sheets[wb.SheetNames[0]];
	if (!ws?.["!ref"]) return [];
	const range = XLSX.utils.decode_range(ws["!ref"]);
	const rows: string[][] = [];
	for (let r = range.s.r; r <= range.e.r; r++) {
		const row: string[] = [];
		for (let c = range.s.c; c <= range.e.c; c++) {
			const cell = ws[XLSX.utils.encode_cell({ r, c })];
			// .h = HTML z formatowaniem fragmentów (bold/italic/strike), .w = sformatowany tekst
			row.push(
				cell ? quoteLinesToHtml(cell.h ?? cell.w ?? String(cell.v ?? "")) : "",
			);
		}
		if (row.some((v) => v.trim())) rows.push(row);
	}
	return rows;
};

const readCsvRows = async (file: Blob): Promise<string[][]> => {
	const Papa = (await import("papaparse")).default;
	return Papa.parse<string[]>(await file.text(), {
		header: false,
		skipEmptyLines: true,
	}).data;
};

// Wiersze arkusza (CSV lub XLSX) jako tablice stringów; XLSX zachowuje formatowanie jako HTML.
// ponytail: pogrubienie całej komórki (styl komórki, nie fragmentu) ginie — SheetJS CE nie czyta styli komórek
export const readSpreadsheetRows = (file: Blob, name: string) =>
	name.toLowerCase().endsWith(".xlsx") ? readXlsxRows(file) : readCsvRows(file);

// Grupuje wiersze w zestawy po kolumnie z nazwą (kolejność pierwszego wystąpienia).
// Pusta komórka dziedziczy nazwę z wiersza wyżej (tytuł wpisany raz / scalone komórki);
// gdy żaden wiersz nie ma nazwy, a jest nagłówek — nazwą zestawu jest nagłówek kolumny.
// Bez kolumny: jeden zestaw z pustą nazwą. Opis = pierwsza niepusta komórka kolumny opisu w grupie.
export const groupRowsByDeck = <T extends Record<string, string>>(
	rows: T[],
	deckNameColumn: string,
	hasHeader: boolean,
	deckDescriptionColumn = "",
): { name: string; description: string; rows: T[] }[] => {
	const byName = new Map<string, T[]>();
	let current = "";
	for (const row of rows) {
		const own = deckNameColumn ? (row[deckNameColumn] ?? "").trim() : "";
		if (own) current = own;
		byName.set(current, [...(byName.get(current) ?? []), row]);
	}
	const groups =
		deckNameColumn && hasHeader && byName.size === 1 && byName.has("")
			? [{ name: deckNameColumn, rows: byName.get("") ?? [] }]
			: [...byName].map(([name, rows]) => ({ name, rows }));
	return groups.map((group) => ({
		...group,
		description: deckDescriptionColumn
			? (group.rows
					.map((row) => (row[deckDescriptionColumn] ?? "").trim())
					.find(Boolean) ?? "")
			: "",
	}));
};
